/**
 * Shared in-memory model cache to dedupe the prefetch + ORT-Web session.create
 * downloads of the same URL.
 *
 * Prior behavior:
 *   1. `main.ts` prefetches each model URL via `fetch(...).arrayBuffer()`,
 *      populating the browser HTTP cache.
 *   2. `loadComponent` later calls `ort.InferenceSession.create(url, ...)`,
 *      which fires its OWN fetch. The browser cache mostly doesn't help here:
 *      ORT-Web's internal fetch uses Range requests on large external-data
 *      files, and Chrome's per-resource disk cache caps out around 100-300 MB
 *      anyway -- our biggest sidecars are 1.5+ GB.
 *
 *   Result: every load downloaded the same .onnx.data twice in parallel
 *   (visible in the Network panel as two same-URL transfers from different
 *   call sites).
 *
 * New behavior:
 *   * `prefetch(url)` initiates a fetch and stashes the resulting Promise so
 *     follow-up calls for the same URL share it.
 *   * `take(url)` awaits the (shared) Promise, returns the ArrayBuffer, and
 *     removes the entry so the bytes can be GC'd once the caller (typically
 *     ORT, which copies into the WASM heap) finishes with them.
 *   * `loadComponent` calls `take(url)` and hands the bytes to
 *     `InferenceSession.create(bytes, ...)` directly -- no second network
 *     fetch from ORT.
 */

const downloads = new Map<string, Promise<ArrayBuffer>>();

/** Start a download for `url` (or return the in-flight Promise if one already
 *  exists). The Promise resolves with the full body bytes. */
export function prefetch(url: string): Promise<ArrayBuffer> {
  let p = downloads.get(url);
  if (!p) {
    p = (async () => {
      const r = await fetch(url, { cache: "force-cache" });
      if (!r.ok) throw new Error(`HTTP ${r.status} ${url}`);
      return r.arrayBuffer();
    })();
    downloads.set(url, p);
  }
  return p;
}

/** Get the bytes for `url`, downloading if needed (or awaiting an in-flight
 *  prefetch). Removes the entry from the cache so the ArrayBuffer can be GC'd
 *  once the caller releases its reference. */
export async function take(url: string): Promise<ArrayBuffer> {
  const p = prefetch(url);
  try {
    return await p;
  } finally {
    downloads.delete(url);
  }
}
